<template>
  <base-slide-over ref="slideover" :title="isNew === true ? 'New Rx Item' : 'Edit Rx Item'" size="xl" style="z-index: 10000">
    <base-form ref="productForm" class="tw-py-2.5 tw-px-4 sm:tw-py-4 sm:tw-px-6" v-model="isFormValid">
      <base-text-field label="Rx Item Name" placeholder="Enter item name" v-model="businessItemCategory.name" class="tw-mt-3" required :maxLength="255" />
      <base-text-field
        label="Short Description"
        placeholder="Enter a short description of the item"
        v-model="businessItemCategory.descriptionShort"
        class="tw-mt-3"
        required
        :maxLength="255"
      />
      <base-textarea
        label="Long Description"
        placeholder="Enter a longer description of the item"
        v-model="businessItemCategory.descriptionLong"
        class="tw-mt-3"
        required
        :maxLength="1020"
      />
      <base-select label="Rx-Item Group" placeholder="Select a group" :options="productGroups" v-model="businessItemCategory.productGroup" />
      <base-text-field
        v-if="businessItemCategory.productGroup === 'new group'"
        label="New Rx Item Group"
        placeholder="Enter new item group"
        v-model="businessItemCategory.newProductGroup"
        :rules="[() => (this.businessItemCategory.productGroup === 'new group' && this.businessItemCategory.newProductGroup !== null) || 'Please enter a new group name']"
        :maxLength="255"
        class="tw-mt-3"
      />
      <base-radio-group :value="businessItemCategory.status" class="tw-mt-3 tw-space-x-6 tw-flex" required>
        <base-radio value="active" name="status" v-model="businessItemCategory.status">Active</base-radio>
        <base-radio value="inactive" name="status" v-model="businessItemCategory.status">Inactive</base-radio>
        <base-radio value="archived" name="status" v-model="businessItemCategory.status">Archived</base-radio>
      </base-radio-group>

      <div class="tw-mt-4 tw-pt-4">
        <!-- All Programs Toggle -->
        <div class="tw-flex tw-items-center tw-mb-2">
          <div class="tw-text-sm tw-mr-2 tw-my-auto tw-text-gray-800 tw-cursor-pointer">All Programs</div>
          <base-toggle v-model="allProgramsToggle" />
        </div>

        <!-- Include/Exclude Radios (only if not 'all') -->
        <div v-if="!allProgramsToggle" class="tw-mt-4">
          <base-radio-group v-model="businessItemCategory.programFilterType" class="tw-space-y-1" required>
            <base-radio value="include" name="programFilterType" v-model="businessItemCategory.programFilterType">Include Programs</base-radio>
            <base-radio value="exclude" name="programFilterType" v-model="businessItemCategory.programFilterType">Exclude Programs</base-radio>
          </base-radio-group>

          <base-advanced-select
            label="Programs"
            placeholder="Select one or more programs"
            :options="formattedPrograms"
            v-model="selectedPrograms"
            label-key="formattedName"
            multiple
          />
        </div>
      </div>
    </base-form>
    <inline-error id="business-plan-error" :title="errorTitle" :message="errorMessage" :error="error" />
    <template #fixed-bottom>
      <div class="tw-py-2 tw-px-3 tw-flex tw-justify-end">
        <base-button type="flat" class="tw-w-auto tw-mr-3" @click="close()">
          Cancel
        </base-button>
        <base-button :loading="isSaving" :disabled="!isFormValid" class="tw-w-auto" @click="save">
          Save
        </base-button>
      </div>
    </template>
  </base-slide-over>
</template>

<script>
import { mapGetters } from "vuex";
import InlineError from "@/components/InlineError";
import { orderBy } from "lodash";

export default {
  name: "BusinessRxItemSlideover",
  components: { InlineError },
  data() {
    return {
      isFormValid: true,
      isSaving: false,
      isNew: false,
      businessId: null,
      businessItemCategory: {
        businessId: null,
        businessItemCategoryId: null,
        name: null,
        descriptionShort: null,
        descriptionLong: null,
        retailPriceCurrency: "USD",
        retailPriceCents: 0,
        retailPrice: 0,
        status: "active",
        type: "item",
        productGroup: null,
        newProductGroup: null,
        taxable: false,
        programFilterType: "include", // Default to 'all'
        programsId: [], // To store selected Program IDs
      },
      programs: [],
      productGroups: [],
      allProgramsToggle: false,
      programFilterOptions: [{ value: "include", label: "Include Programs" }, { value: "exclude", label: "Exclude Programs" }],
      selectedPrograms: [],
      errorTitle: "",
      errorMessage: "",
      error: null,
    };
  },
  computed: {
    ...mapGetters("organization", ["organization"]),
    formattedPrograms() {
      const sortedPrograms = orderBy(this.programs, ["programGroup", "name"]);
      return sortedPrograms.map((program) => {
        let cleanedProgramGroup = program.programGroup;
        if (cleanedProgramGroup) {
          // Regex to remove patterns like "1 - ", "2- ", etc. from the beginning of the string
          cleanedProgramGroup = cleanedProgramGroup.replace(/^\d+\s*-\s*/, "");
        }

        return {
          ...program,
          formattedName: program.name + (cleanedProgramGroup ? ` - ${cleanedProgramGroup}` : ""),
        };
      });
    },
  },
  methods: {
    reset() {
      this.isNew = false;
      this.businessId = null;
      this.businessItemCategory = {
        businessId: null,
        businessItemCategoryId: null,
        name: null,
        descriptionShort: null,
        descriptionLong: null,
        retailPriceCurrency: "USD",
        retailPriceCents: 0,
        retailPrice: 0,
        status: "active",
        type: "rx_item",
        productGroup: null,
        newProductGroup: null,
        taxable: false,
        programFilterType: "include", // Default to 'all'
        programsId: [],
      };
      this.errorTitle = "";
      this.errorMessage = "";
      this.error = null;
      this.allProgramsToggle = false;
      this.selectedPrograms = [];
    },
    async show(businessId, businessItemCategory, productGroups, programs) {
      this.reset();
      this.businessId = businessId;
      this.productGroups = productGroups;

      this.programs = programs || [];

      if (!businessItemCategory) {
        this.isNew = true;
      } else {
        this.isNew = false;
        this.businessItemCategory.businessId = businessId;
        this.businessItemCategory.businessItemCategoryId = businessItemCategory.businessItemCategoryId;
        this.businessItemCategory.name = businessItemCategory.name;
        this.businessItemCategory.descriptionShort = businessItemCategory.descriptionShort;
        this.businessItemCategory.descriptionLong = businessItemCategory.descriptionLong;
        this.businessItemCategory.retailPriceCurrency = businessItemCategory.retailPriceCurrency;
        this.businessItemCategory.retailPrice = businessItemCategory.retailPriceCents;
        this.businessItemCategory.status = businessItemCategory.status;
        this.businessItemCategory.type = businessItemCategory.type;
        this.businessItemCategory.productGroup = businessItemCategory.productGroup;
        this.businessItemCategory.taxable = businessItemCategory.taxable;

        this.allProgramsToggle = businessItemCategory.programFilterType === "all";
        this.businessItemCategory.programFilterType = businessItemCategory.programFilterType || "all";
        // Save raw programsId for watcher
        this.businessItemCategory.programsId = businessItemCategory.programsId || "[]";
        if (businessItemCategory.programsId) {
          try {
            const parsedProgramIds = typeof businessItemCategory.programsId === "string" ? JSON.parse(businessItemCategory.programsId) : businessItemCategory.programsId;
            this.selectedPrograms = this.formattedPrograms.filter((prog) => parsedProgramIds.includes(prog.businessProgramId));
          } catch (e) {
            this.selectedPrograms = [];
          }
        } else {
          this.selectedPrograms = [];
        }
      }

      this.$refs.slideover.show();
    },
    close() {
      this.$refs.slideover.hide();
    },
    save() {
      this.businessItemCategory.programsId = JSON.stringify(this.selectedPrograms.map((prog) => prog.businessProgramId));
      this.isSaving = true;
      if (!this.$refs.productForm.validate()) {
        this.isSaving = false;
        return;
      }

      const productGroupToSave = this.businessItemCategory.productGroup === "new group" ? this.businessItemCategory.newProductGroup : this.businessItemCategory.productGroup;

      const payload = {
        businessId: this.businessId,
        name: this.businessItemCategory.name,
        descriptionShort: this.businessItemCategory.descriptionShort,
        descriptionLong: this.businessItemCategory.descriptionLong,
        retailPriceCurrency: this.businessItemCategory.retailPriceCurrency,
        retailPriceCents: this.businessItemCategory.retailPriceCents,
        status: this.businessItemCategory.status,
        taxable: this.businessItemCategory.taxable,
        type: this.businessItemCategory.type,
        productGroup: productGroupToSave,
        programsId: this.businessItemCategory.programsId,
        programFilterType: this.businessItemCategory.programFilterType,
      };

      if (this.isNew) {
        this.$axios
          .post(`/businesses/${this.businessId}/item-categories`, payload)
          .then(() => {
            this.close();
            this.$emit("save");
            this.EventBus.$emit("GLOBAL_NOTIFY", { title: "Rx-Item Added", message: "Rx-Item " + this.businessItemCategory.name + " was created successfully" });
          })
          .catch((err) => {
            this.errorTitle = `Failed to create ${this.businessItemCategory.name}`;
            this.errorMessage = `Rx-Item ${this.businessItemCategory.name} was not able to be created. Please try again.`;
            this.error = err;
          })
          .finally(() => {
            this.isSaving = false;
          });
      } else {
        payload.businessItemCategoryId = this.businessItemCategory.businessItemCategoryId;
        this.$axios
          .put(`/businesses/${this.businessId}/item-categories/${this.businessItemCategory.businessItemCategoryId}`, payload)
          .then(() => {
            this.close();
            this.$emit("save");
            this.EventBus.$emit("GLOBAL_NOTIFY", { title: "Rx Item Updated", message: "Rx Item " + this.businessItemCategory.name + " was updated successfully" });
          })
          .catch((err) => {
            this.errorTitle = `Failed to update ${this.businessItemCategory.name}`;
            this.errorMessage = `Rx Item ${this.businessItemCategory.name} was not able to be updated. Please try again.`;
            this.error = err;
          })
          .finally(() => {
            this.isSaving = false;
          });
      }
    },
  },

  watch: {
    programs: {
      handler(newVal) {
        // Only re-set if programs have loaded and businessItemCategory data is present for edit mode
        if (!this.isNew && this.businessItemCategory.businessItemCategoryId && newVal.length > 0 && this.businessItemCategory.programsId) {
          try {
            const parsed = typeof this.businessItemCategory.programsId === "string" ? JSON.parse(this.businessItemCategory.programsId) : this.businessItemCategory.programsId;
            // Ensure we filter from the formattedPrograms if this watcher ever needs to trigger
            // after the component is already active and `formattedPrograms` is computed.
            this.selectedPrograms = this.formattedPrograms.filter((prog) => parsed.includes(prog.businessProgramId));
          } catch (e) {
            this.selectedPrograms = [];
          }
        }
      },
      deep: true,
      immediate: true,
    },
    allProgramsToggle(newValue) {
      if (newValue) {
        this.businessItemCategory.programFilterType = "all";
        this.selectedPrograms = [];
      } else {
        if (this.businessItemCategory.programFilterType === "all") {
          this.businessItemCategory.programFilterType = "include";
        }
      }
    },
    "businessItemCategory.programFilterType"(newVal) {
      this.allProgramsToggle = newVal === "all";
    },
  },
};
</script>
